import { memo, useCallback, useMemo } from 'react';
import { Box, makeStyles, IconButton } from '@material-ui/core';
import {
  useSortable,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { SText } from '@setvi/shared/components';
import {
  QuoteSectionDTO,
  QuoteProductDTO
} from '@setvi/shared/services/react-query';
import { DragHandleIcon } from '@setvi/shared/components/sicons';
import { DraggableItem } from './draggable-item';
import { DraggableProductItem } from './draggable-product-item';
import { assignPositions } from './index';

const useStyles = makeStyles(() => ({
  sectionContainer: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#EEF0F2',
    borderRadius: '16px',
    overflow: 'hidden'
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '0 12px',
    cursor: 'grab',
    '&:active': {
      cursor: 'grabbing'
    }
  },
  dragHandle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '40px',
    color: '#5D7083',
    cursor: 'grab',
    '&:active': {
      cursor: 'grabbing'
    }
  },
  headerContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    padding: '8px 0'
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '24px',
    color: '#333D47'
  },
  subtitle: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: '#333D47'
  },
  expandButton: {
    padding: '8px',
    color: '#5D7083'
  },
  childrenContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '0 12px 12px 12px'
  },
  childItem: {
    marginLeft: '32px'
  },
  isDragging: {
    opacity: 0.5
  },
  dragOverlay: {
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
  },
  emptyChildren: {
    padding: '12px',
    marginLeft: '32px',
    color: '#5D7083',
    fontSize: '14px',
    fontStyle: 'italic'
  }
}));

interface SectionChildrenListProps {
  sectionChildren: QuoteProductDTO[];
  sectionId: string;
  onChildrenReorder: (sectionId: string, children: QuoteProductDTO[]) => void;
  onProductChildrenReorder?: (
    sectionId: string,
    productId: string,
    children: QuoteProductDTO[]
  ) => void;
}

const SectionChildrenList = memo(
  ({
    sectionChildren,
    sectionId,
    onChildrenReorder,
    onProductChildrenReorder
  }: SectionChildrenListProps) => {
    const classes = useStyles();

    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          distance: 8
        }
      }),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates
      })
    );

    const childIds = useMemo(
      () => sectionChildren.map(child => child.id || child._id),
      [sectionChildren]
    );

    const handleChildDragEnd = useCallback(
      (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
          const oldIndex = sectionChildren.findIndex(
            child => (child.id || child._id) === active.id
          );
          const overIndex = sectionChildren.findIndex(
            child => (child.id || child._id) === over.id
          );

          if (oldIndex === -1 || overIndex === -1) return;

          const without = sectionChildren.filter((_, i) => i !== oldIndex);
          const insertIdx = overIndex;

          const result = [...without];
          result.splice(insertIdx, 0, sectionChildren[oldIndex]);
          onChildrenReorder(sectionId, assignPositions(result));
        }
      },
      [sectionChildren, sectionId, onChildrenReorder]
    );

    const handleProductChildrenReorder = useCallback(
      (productId: string, children: QuoteProductDTO[]) => {
        onProductChildrenReorder?.(sectionId, productId, children);
      },
      [sectionId, onProductChildrenReorder]
    );

    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleChildDragEnd}>
        <SortableContext
          items={childIds}
          strategy={verticalListSortingStrategy}>
          {sectionChildren.map(child => {
            const childId = child.id || child._id;
            const hasProductChildren =
              child.children && child.children.length > 0;

            return (
              <Box key={childId} className={classes.childItem}>
                {hasProductChildren ? (
                  <DraggableProductItem
                    product={child}
                    onChildrenReorder={handleProductChildrenReorder}
                  />
                ) : (
                  <DraggableItem item={child} />
                )}
              </Box>
            );
          })}
        </SortableContext>
      </DndContext>
    );
  }
);

interface DraggableSectionItemProps {
  section: QuoteSectionDTO;
  isExpanded: boolean;
  onToggle: (sectionId: string) => void;
  onChildrenReorder: (sectionId: string, children: QuoteProductDTO[]) => void;
  onProductChildrenReorder?: (
    sectionId: string,
    productId: string,
    children: QuoteProductDTO[]
  ) => void;
  isDragOverlay?: boolean;
}

export const DraggableSectionItem = memo(
  ({
    section,
    isExpanded,
    onToggle,
    onChildrenReorder,
    onProductChildrenReorder,
    isDragOverlay = false
  }: DraggableSectionItemProps) => {
    const classes = useStyles();
    const sectionId = section.id || section._id;

    const children = useMemo(
      () => (section.children || []) as QuoteProductDTO[],
      [section.children]
    );

    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging
    } = useSortable({
      id: sectionId,
      disabled: isDragOverlay
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition
    };

    const handleToggle = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onToggle(sectionId);
      },
      [sectionId, onToggle]
    );

    const hasChildren = children.length > 0;

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`${classes.sectionContainer} ${isDragging ? classes.isDragging : ''} ${isDragOverlay ? classes.dragOverlay : ''}`}>
        <div className={classes.sectionHeader} {...attributes} {...listeners}>
          <Box className={classes.dragHandle}>
            <DragHandleIcon style={{ fontSize: '12px' }} />
          </Box>
          <Box className={classes.headerContent}>
            <SText
              title={section.name || 'Unnamed Section'}
              weight="semibold"
              className={classes.title}
              maxLines={1}
            />
            {section.note && (
              <SText
                title={section.note}
                className={classes.subtitle}
                maxLines={1}
              />
            )}
          </Box>
          {hasChildren && !isDragOverlay && (
            <IconButton
              className={classes.expandButton}
              onClick={handleToggle}
              size="small">
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
          {hasChildren && isDragOverlay && (
            <Box className={classes.expandButton}>
              <KeyboardArrowDownIcon />
            </Box>
          )}
        </div>

        {isExpanded && hasChildren && !isDragOverlay && (
          <Box className={classes.childrenContainer}>
            <SectionChildrenList
              sectionChildren={children}
              sectionId={sectionId}
              onChildrenReorder={onChildrenReorder}
              onProductChildrenReorder={onProductChildrenReorder}
            />
          </Box>
        )}

        {!hasChildren && isExpanded && !isDragOverlay && (
          <Box className={classes.emptyChildren}>No items in this section</Box>
        )}
      </div>
    );
  }
);
